/**
 * React Hook for Hono RPC Client
 *
 * Provides a memoized RPC client instance with Clerk authentication.
 *
 * @module lib/rpc/useRpc
 */

import { useMemo, useRef } from 'react';

import { useAuth } from '@/lib/clerk';
import { logger } from '@/lib/logger';
import { createRpcClient, type RpcClient } from '@/lib/rpc/client';

/**
 * React hook for using the Hono RPC client
 *
 * This hook provides a memoized RPC client instance that automatically
 * handles Clerk authentication token retrieval. The client instance is
 * stable across renders to prevent unnecessary re-renders.
 *
 * @returns Memoized RpcClient instance with Clerk authentication
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const rpc = useRpc();
 *
 *   const handleClick = async () => {
 *     // All routes are type-safe!
 *     const res = await rpc.api.private.tables.$get();
 *     if (res.ok) {
 *       const data = await res.json();
 *       console.log(data);
 *     }
 *   };
 *
 *   return <button onClick={handleClick}>Fetch Tables</button>;
 * }
 * ```
 */
export function useRpc(): RpcClient {
  const { getToken } = useAuth();

  // Use ref to keep getToken reference stable for the RPC client
  // This prevents recreating the client on every render
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;

  // Create stable token getter that uses the ref
  const stableGetToken = useMemo(
    () => async () => {
      try {
        const token = await getTokenRef.current();

        // Check if token is null (no error, but no token available)
        if (token === null) {
          logger.warn(
            'No token returned from Clerk, user may need to re-authenticate'
          );
        }

        return token;
      } catch (error) {
        logger.warn(
          'Failed to get auth token from Clerk, user may need to re-authenticate',
          {
            error:
              error instanceof Error
                ? { message: error.message, stack: error.stack }
                : error,
          }
        );
        // Return null to let requests proceed - API will return 401 which app can handle
        return null;
      }
    },
    []
  );

  // RPC client is created once and stays stable
  return useMemo(() => createRpcClient(stableGetToken), [stableGetToken]);
}
